import { useEffect, useState } from "react";
import { useI18n, type StringKey } from "../i18n";
import { loadAlerts, loadApprovals, type Alert } from "../data";
import type { Workspace } from "../types";

const tone: Record<Alert["kind"], string> = {
  contract_end: "warning", probation_end: "warning", document_expiring: "warning", document_expired: "danger",
  documents_overdue: "danger", birthday: "info",
};

/** Home panel: decisions waiting for you, and dates coming up for your people. */
export function NeedsAttention({ ws, onOpen }: { ws: Workspace; onOpen: (id: string) => void }) {
  const { t, pick } = useI18n();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [waiting, setWaiting] = useState(0);
  useEffect(() => {
    if (ws.enabled.has("alerts")) void loadAlerts(ws.membership.company_id).then(setAlerts).catch(() => setAlerts([]));
    if (ws.enabled.has("leave") && ws.enabled.has("approvals")) void loadApprovals().then((a) => setWaiting(a.length)).catch(() => setWaiting(0));
  }, [ws.membership.company_id, ws.enabled]);

  if (alerts.length === 0 && waiting === 0) return null;

  const when = (n: number) =>
    n === 0 ? t("today") : n > 0 ? t("inDays").replace("{n}", String(n)) : t("daysAgo").replace("{n}", String(-n));

  return (
    <section className="card">
      <h2>{t("needsAttentionTitle")}</h2>
      {waiting > 0 && (
        <div className="row waiting">
          <span className="stat-value">{waiting}</span>
          <span>{t("waitingForYou")}</span>
          <button type="button" className="btn primary small" onClick={() => onOpen("approvals")}>{t("openApprovals")}</button>
        </div>
      )}
      {alerts.length > 0 && (
        <ul className="alert-list">
          {alerts.slice(0, 8).map((a) => (
            <li key={`${a.kind}-${a.employment_id}-${a.due_date}`}>
              <span className={`badge ${tone[a.kind]}`}>{t(`alert_${a.kind}` as StringKey)}</span>
              <button type="button" className="link" onClick={() => onOpen(`employee-${a.employment_id}`)}>{pick(a.full_name, a.full_name_ar)}</button>
              {a.kind === "documents_overdue" ? <span className="muted">{a.detail}</span> : a.detail && <span className="muted">{a.detail}</span>}
              <span className="mono muted alert-when">{when(a.days_left)}</span>
            </li>
          ))}
        </ul>
      )}
      {alerts.length > 8 && <button type="button" className="link" onClick={() => onOpen("alerts")}>{t("alertsSeeAll").replace("{n}", String(alerts.length))}</button>}
    </section>
  );
}
